import { Box, Container, Link, styled, Typography } from "@mui/material";
import React from "react";
import { Footer } from "../allComponents/AllComponents";

const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  fontFamily: "'Inter', sans-serif",
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const PrivacyPolicy = () => {
  return (
    <React.Fragment>
      <PageContainer maxWidth="md">
        <Typography
          variant="h3"
          gutterBottom
          sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}
        >
          Privacy Policy
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Last updated: July 18, 2026
        </Typography>

        <Section>
          <Typography variant="h5" gutterBottom>
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            This Privacy Policy describes how we collect, use, disclose, and
            protect personal information when you use our websites, applications,
            and services (collectively, the “Service”). It applies to both of our
            products:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                <b>FundsyFi Circle</b> — our group savings and
                rotating-contribution platform for individuals.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>FundsyFi Recover</b> — an AI-assisted accounts-receivable and
                revenue-recovery platform for businesses that connects to
                accounting systems such as QuickBooks Online and supports invoice
                reminders, customer communications, payment-promise tracking,
                lead intelligence, and sales outreach.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph>
            The Service is operated by FundsyFi (legal entity:{" "}
            <b>[Insert exact registered legal name — e.g., FundsyFi, LLC]</b>),
            located in Houston, Texas 77085 (“FundsyFi,” “we,” “us,” or “our”).
            You can reach us at{" "}
            <Link href="mailto:fundsyfi@fundsyfi.com">fundsyfi@fundsyfi.com</Link>{" "}
            or{" "}
            <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>.
          </Typography>
          <Typography variant="body1" paragraph>
            This Privacy Policy is a notice describing our processing practices.
            Where a particular activity requires your consent — such as certain
            SMS messages, optional marketing cookies, or specific marketing
            communications — we obtain that consent separately.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            1.1 Who This Policy Covers
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">Website visitors</Typography>
            </li>
            <li>
              <Typography variant="body1">
                FundsyFi account holders (Circle members and Recover business
                users)
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Employees and authorized representatives of FundsyFi business
                customers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Invoice recipients (our business customers’ customers) and their
                representatives
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Prospective customers and sales contacts
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                People we connect with through professional platforms such as
                LinkedIn
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Job applicants and support contacts, where applicable
              </Typography>
            </li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            1.2 Our Role (Controller / Processor)
          </Typography>
          <Typography variant="body1" paragraph>
            For our own website visitors, account holders, and sales and
            marketing activities, FundsyFi generally determines the purposes of
            processing (a “controller” or “business”). For information about a
            business customer’s own customers and invoice recipients that we
            process to provide FundsyFi Recover, the FundsyFi business customer
            ordinarily determines why that information is processed, and FundsyFi
            processes it on their behalf to provide the Service (a “processor” or
            “service provider”). The exact controller/processor characterization
            depends on the activity and jurisdiction and should be reviewed for
            each.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            2. Information We Collect
          </Typography>

          <Typography variant="h6" gutterBottom>
            2.1 Information You Provide
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Account registration: name, business name, email, phone number,
                and (for Circle) date of birth
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Payment method details — see “Payments” below
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Identity verification: government-issued ID images (via Stripe
                Identity), where required
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Profile and preferences: username, avatar, workspace and product
                settings
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Content you submit: support requests, survey responses, uploaded
                lead lists, message templates, and campaign settings
              </Typography>
            </li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            2.2 Information Collected Automatically
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Usage data: pages visited, features used, timestamps
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Device and browser: IP address, device type, operating system,
                browser version
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Cookies and similar technologies — see “Cookies and Analytics”
                and our Cookie Policy
              </Typography>
            </li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            2.3 QuickBooks Online Data (FundsyFi Recover)
          </Typography>
          <Typography variant="body1" paragraph>
            When a business connects its QuickBooks Online (“QBO”) account, we
            access and synchronize the following categories of accounting data to
            provide the Service:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                QuickBooks company profile information
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Customers and customer contact details
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Invoices, balances, due dates, and aging information
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Payments and payment statuses
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Credit memos and related transaction information
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Accounting user or authorization identifiers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                OAuth access and refresh tokens
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                QBO connection and synchronization logs
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ mt: 1 }}>
            <b>How we use QBO data.</b> We use this data solely to provide
            FundsyFi Recover — to identify overdue invoices, send reminders and
            communications on the business’s behalf, track payment promises,
            surface analytics, and keep the business’s workspace in sync with
            QuickBooks. We do not use QBO data for advertising, and we do not sell
            it.
          </Typography>
          <Typography variant="body1" paragraph>
            <b>Data in reminders.</b> Reminder and communication messages
            typically include the customer name, invoice number, amount due, due
            date, and a secure payment link. We do not include full QuickBooks
            records in customer-facing messages.
          </Typography>
          <Typography variant="body1" paragraph>
            <b>AI processing of QBO data.</b> Limited context (for example, an
            invoice number, balance, due date, and a customer’s reply text) may be
            sent to our AI service provider to classify inbound replies and help
            route them. We do not send full QuickBooks records, stored
            authentication tokens, or bulk exports to AI providers. See “Artificial
            Intelligence” below.
          </Typography>
          <Typography variant="body1" paragraph>
            <b>Retention, disconnection, and deletion.</b> We retain QBO data
            while the connection is active and the business uses the Service, and
            for a limited period afterward as described in “Data Retention” below.
            When a business disconnects QuickBooks, we stop syncing, revoke and
            delete the stored OAuth tokens, and delete or deidentify synced QBO
            records within a commercially reasonable period, except where limited
            records must be retained for legal, security, or accounting purposes.
            A business may request deletion of its QBO data at any time by
            contacting{" "}
            <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>.
          </Typography>
          <Typography variant="body1" paragraph>
            <b>Model training.</b> We do not use QBO data to train FundsyFi’s own
            models, and we use AI providers under terms that do not permit them to
            use our prompts or responses to train their general models. See
            “Artificial Intelligence” below.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            2.4 Information About Our Business Customers’ Customers (Invoice
            Recipients)
          </Typography>
          <Typography variant="body1" paragraph>
            To provide FundsyFi Recover, we process information about invoice
            recipients who may never create a FundsyFi account, including:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Customer and business names
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Business email addresses and phone numbers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Invoice numbers, amounts owed, and due dates
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Payment history and statuses
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Customer replies, promise-to-pay dates, and dispute reasons
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                SMS and email communications exchanged through the Service
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ mt: 1 }}>
            The FundsyFi business customer ordinarily determines why this
            information is processed; FundsyFi processes it to provide the Service
            on their behalf. Invoice recipients may contact us at{" "}
            <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>{" "}
            and we will route requests to the relevant business customer where
            appropriate.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            2.5 Lead-Intelligence and Sales-Prospect Data (FundsyFi Recover)
          </Typography>
          <Typography variant="body1" paragraph>
            FundsyFi may collect professional and business-contact information to
            identify and reach potential business customers. Sources include:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Apollo and other licensed data providers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Business websites and public company directories
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Public professional profiles and public job postings
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Business registries and industry associations
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Search providers and user-submitted lead lists
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Approved LinkedIn APIs, where available
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ mt: 1 }}>
            The categories may include name, employer, job title, department and
            seniority, business email, business phone, company address,
            professional-profile URL, company size and industry, publicly
            available company information, outreach history, and campaign
            engagement. We may also generate <b>inferred</b> data points — such as
            likelihood of using QuickBooks, invoicing/accounts-receivable
            relevance, and a lead-qualification score.{" "}
            <b>Inferred information is an estimate, not a confirmed fact.</b>
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            3. How We Use Information
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Provide and improve the Service: authenticate users, sync
                accounting data, send communications, and operate product
                features
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Payments and billing: process subscription payments and manage
                billing status
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Security and compliance: verify identity, prevent fraud and abuse,
                and maintain audit and security logs
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Communications: invoice reminders (on behalf of business
                customers), account notifications, support, and product updates
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Sales and marketing: identify and contact prospective business
                customers and measure engagement
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Analytics and product development: understand usage and improve
                features
              </Typography>
            </li>
          </Box>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            4. Artificial Intelligence
          </Typography>
          <Typography variant="body1" paragraph>
            FundsyFi Recover uses artificial intelligence to help operate certain
            features. This section explains how.
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                <b>What data may be processed by AI.</b> Limited context such as a
                customer’s reply message, invoice number, balance, due date, and
                lead/prospect attributes.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Purposes.</b> Classifying inbound replies, extracting
                payment-promise dates, generating lead scores and classifications,
                and assisting with drafting reminders or sales messages.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Human review.</b> Significant or lower-confidence outputs are
                routed to a human for review. AI does not independently send
                customer-facing communications without the applicable rules,
                confidence thresholds, or human review configured by the business.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Model training.</b> We do not use your data or QBO data to
                train FundsyFi’s own models, and we use AI providers under terms
                that do not permit them to use our prompts or responses to train
                their general models. We honor the confidentiality and training
                representations we make.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Providers and retention.</b> Prompts and responses are
                processed by AI and machine-learning service providers and
                retained only for a limited period for abuse-monitoring and
                service operation, consistent with our providers’ policies.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Accuracy.</b> AI outputs, scores, and inferences are estimates
                and may be inaccurate. You may ask us to review or correct
                inaccurate information as described in “Your Privacy Rights.”
              </Typography>
            </li>
          </Box>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            5. Payments
          </Typography>
          <Typography variant="body1" paragraph>
            Payment method details are collected and processed by Stripe. FundsyFi
            may receive limited payment-related information, such as payment method
            type, the last four digits of a card, billing status, and transaction
            identifiers. We do not store full payment card numbers.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            6. How We Share Information
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell your personal information. We share information with
            categories of service providers and recipients that help us operate
            the Service, subject to contractual restrictions:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Cloud and database hosting
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Accounting-platform integrations (e.g., QuickBooks Online)
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Payment processors (e.g., Stripe) and identity-verification
                providers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Email and SMS delivery providers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Customer-support, analytics, and cookie providers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                AI and machine-learning providers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Data-enrichment and verification providers, and CRM /
                sales-outreach providers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Professional platforms such as LinkedIn, where authorized
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Security, fraud-prevention, and monitoring providers
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Legal and professional advisers, and authorities where required by
                law, subpoena, or court order
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                A successor entity in the event of a merger, acquisition, or asset
                sale, under equivalent protections
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ mt: 1 }}>
            A current list of key subprocessors is available on request at{" "}
            <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            7. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain personal information only as long as necessary for the
            purposes described in this policy. Retention varies by category:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Account information — while the account is active and for a
                limited period afterward.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                QBO accounting information — while QuickBooks is connected and for
                a limited period after disconnection or account closure.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                OAuth tokens — deleted promptly when QuickBooks is disconnected or
                access is revoked.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Invoice and communication records — retained to operate the
                Service and for legal and accounting purposes.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Lead and prospect information — retained while relevant to
                outreach, subject to removal/suppression requests.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Email suppression and SMS consent records — retained as needed to
                honor opt-outs and prevent future unwanted contact.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Security logs, audit records, and transaction records — retained
                for fraud-prevention, security, legal, and accounting purposes.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                AI prompts and outputs — retained only for a limited period as
                described in “Artificial Intelligence.”
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Backups — deleted on a rolling schedule; deletion from backups may
                take additional time after deletion from active systems.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ mt: 1 }}>
            When an account is closed or QuickBooks is disconnected, we delete or
            deidentify the associated data within a commercially reasonable period,
            except where we must retain limited records for legal, fraud-prevention,
            accounting, or suppression purposes. Where we retain deidentified or
            aggregated data, we apply measures intended to prevent reidentification
            and use it for analytics, security, and product improvement. We do not
            promise complete deletion where retention is legally required.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            8. SMS and Messaging
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on the context, messaging may include: (a) FundsyFi account
            notifications; (b) invoice reminders sent on behalf of a FundsyFi
            business customer; (c) FundsyFi sales or marketing texts; and (d)
            one-to-one support communications. Each may involve different consent
            and compliance requirements. Message frequency varies and message and
            data rates may apply. You can opt out at any time by replying STOP, and
            reply HELP for assistance.
          </Typography>
          <Typography variant="body1" paragraph>
            <b>
              We do not sell mobile opt-in information or disclose it to third
              parties for their independent marketing or promotional purposes.
            </b>{" "}
            We may provide it to service providers that help us deliver and manage
            messaging (for example, our SMS provider, which must receive phone
            numbers and message content to deliver messages), subject to
            contractual restrictions.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            9. Sales-Engagement Tracking
          </Typography>
          <Typography variant="body1" paragraph>
            For sales and marketing activities, FundsyFi may record: emails sent
            and delivery status, bounces, replies, link interactions, unsubscribes,
            LinkedIn requests prepared or sent, connection acceptances (where
            available or manually recorded), LinkedIn messages and replies where
            authorized, meetings and demonstrations, and trial/subscription
            attribution. Email opens are not always reliable, and automated
            security scanners may generate false click or open events.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            10. Cookies and Analytics
          </Typography>
          <Typography variant="body1" paragraph>
            We use cookies and similar technologies across the following
            categories:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                <b>Strictly necessary</b> — required for the site and app to
                function; on by default.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Functional</b> — remember preferences and settings.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Analytics</b> — help us understand usage.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Advertising / targeting</b> — used only where applicable and
                with consent where required.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ mt: 1 }}>
            Where required by law, nonessential cookies operate only after you
            consent, and you can withdraw consent at any time (through our consent
            manager where offered, or your browser settings). We may also use email
            open- and click-tracking in our communications. See our{" "}
            <Link href="/cookies">Cookie Policy</Link> for details.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            11. Security
          </Typography>
          <Typography variant="body1" paragraph>
            We use administrative, technical, and organizational safeguards
            designed to protect personal information. No method of transmission or
            storage is completely secure. Controls we operate include encryption of
            sensitive data in transit and of stored access tokens, access controls,
            logging and audit trails, secrets management, backups, and
            authentication. Payment card data is handled by Stripe.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            12. International Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            We operate primarily in the United States, and your information may be
            processed there. Where we transfer personal information across borders
            and an appropriate transfer mechanism is required, we implement one
            based on our actual contractual arrangements with the relevant
            providers. For details relevant to your jurisdiction (including UK or
            Canadian personal data), contact{" "}
            <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            13. Your Privacy Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on your jurisdiction, you may have rights to access, correct,
            delete, or obtain a portable copy of your personal information, and to
            opt out of targeted advertising, sale, or certain profiling. To submit a
            request, email{" "}
            <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>.
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                <b>Verification.</b> We may need to verify your identity before
                acting on a request.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Authorized agents.</b> You may use an authorized agent, subject
                to verification.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Timing and appeals.</b> We respond within the timeframes
                required by applicable law, and you may appeal a denied request by
                replying to our decision.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Correction.</b> You may ask us to correct inaccurate
                information, including AI-generated inferences.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <b>Non-users.</b> People who appear in our prospect database can
                request access, correction, removal, objection, or suppression as
                described in “Prospect Data” below.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ mt: 1 }}>
            <b>Texas residents.</b> Texas residents may have rights concerning
            access, correction, deletion, data portability, and certain opt-outs
            under the Texas Data Privacy and Security Act, subject to its
            applicability and exceptions.
          </Typography>
          <Typography variant="body1" paragraph>
            <b>California residents.</b> California residents may have rights under
            the CCPA/CPRA, including to know the categories of personal information
            collected, sources, purposes, and recipients, and to access, delete,
            correct, and opt out of sale or sharing. We do not sell personal
            information. To exercise these rights, contact{" "}
            <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            14. Prospect Data: Sources, Accuracy &amp; Removal
          </Typography>
          <Typography variant="body1" paragraph>
            If you received outreach from us and want to know where we obtained
            your information, the source categories are described in “Lead-
            Intelligence and Sales-Prospect Data” above (for example, licensed data
            providers such as Apollo, public directories and profiles, business
            registries, and search providers). Because we may hold information about
            people who did not directly provide it to us, you can ask us to:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body1">
                Access the information we hold about you
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Correct inaccurate information
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Remove your information from our database
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Object to processing or suppress future outreach
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ mt: 1 }}>
            Submit requests to{" "}
            <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>. We
            may retain minimal suppression data to ensure we do not contact you
            again.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            15. Children’s Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            Our Service is for users 18 or older. We do not knowingly collect data
            from children under 18. If discovered, we will delete such data
            promptly.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            16. Changes to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this policy. If material changes occur, we will notify you
            by email or in-app. The “Last updated” date above reflects the most
            recent version.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" gutterBottom>
            17. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            Questions or requests? Contact us at:
          </Typography>
          <Typography variant="body1">
            <Link href="mailto:fundsyfi@fundsyfi.com">fundsyfi@fundsyfi.com</Link>{" "}
            ·{" "}
            <Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>
          </Typography>
          <Typography variant="body1">
            FundsyFi — <b>[Insert exact registered legal name]</b>
          </Typography>
          <Typography variant="body1">Houston, Texas 77085</Typography>
        </Section>
      </PageContainer>
      <Footer />
    </React.Fragment>
  );
};

export default PrivacyPolicy;
